// helper to load any image URL
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img), { once: true });
    img.addEventListener('error', () => reject(new Error(`Failed to load image at ${url}`)), { once: true });
    img.src = url;
  });
}

const getPreferredImageMime = () => {
  const canvas = document.createElement('canvas');
  const supportsWebP = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  return supportsWebP ? 'image/webp' : 'image/png';
};

/**
 * Crop an image and return a File (PNG) or Blob.
 *
 * @param {string} imageSrc - source URL or data URI
 * @param {{ x:number, y:number, width:number, height:number }} pixelCrop
 * @param {object} [opts]
 * @param {string} [opts.background='#fff']
 */
export default async function getCroppedImg(imageSrc, pixelCrop, { background = '#fff' } = {}) {
  const mime = getPreferredImageMime();
  const fileName = mime === 'image/webp' ? 'cropped.webp' : 'cropped.png';

  // 1. Load image
  const img = await loadImage(imageSrc);

  // 2. Setup crop parameters
  const { width: cw, height: ch, x, y } = pixelCrop;
  const maxHeight = 1000;

  // 3. Limit height and adjust width proportionally
  let targetWidth = cw;
  let targetHeight = ch;
  if (ch > maxHeight) {
    const scale = maxHeight / ch;
    targetHeight = Math.round(ch * scale);
    targetWidth = Math.round(cw * scale);
  }

  // 4. Setup canvas with DPI scaling
  const ratio = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth * ratio;
  canvas.height = targetHeight * ratio;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.scale(ratio, ratio);

  // 5. Fill background
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }

  // 6. Compute source and destination dimensions
  const sw = img.width;
  const sh = img.height;
  const sx = Math.max(0, x);
  const sy = Math.max(0, y);
  const sWidth = Math.max(0, Math.min(cw, sw - sx));
  const sHeight = Math.max(0, Math.min(ch, sh - sy));
  const dx = x < 0 ? -x : 0;
  const dy = y < 0 ? -y : 0;

  // 7. Draw the image, scaled to fit the limited size
  if (sWidth && sHeight) {
    ctx.drawImage(img, sx, sy, sWidth, sHeight, dx, dy, targetWidth, targetHeight);
  }

  // 8. Export to blob
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      fileBlob => {
        if (fileBlob) {
          resolve(fileBlob);
        } else {
          reject(new Error('Blob creation failed'));
        }
      },
      mime,
      0.9, // quality for JPEG/WebP
    );
  });

  // 9. Return final File
  return new File([blob], fileName, { type: mime });
}
